import React, { useEffect, useRef, useState } from 'react'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, MarkdownEditor } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { useOrgBranding } from '@/hooks/useOrgBranding'
import type { PostSubmitMessage } from '@/store/workspaceAtoms'

interface PostSubmitMessageDialogProps {
  message: PostSubmitMessage | null
  onClosed: () => void
}

const SHRINK_MS = 320

/**
 * Partner's submit message. On close it shrinks toward the card's lightbulb
 * button (located by data-challenge-id) so the user sees where it went.
 */
export const PostSubmitMessageDialog: React.FC<PostSubmitMessageDialogProps> = ({ message, onClosed }) => {
  const { t } = useTar()
  const { branding } = useOrgBranding(message?.organizationId)
  const contentRef = useRef<HTMLDivElement>(null)
  const [closing, setClosing] = useState(false)

  const open = message !== null

  // Reset only on next open so the shrink class holds through Radix's exit animation.
  useEffect(() => {
    if (message !== null) setClosing(false)
  }, [message])

  const close = () => {
    if (closing) return
    const content = contentRef.current
    // The button is rendered twice per card (desktop and mobile clusters); only one is visible.
    const target = message
      ? Array.from(
        document.querySelectorAll<HTMLElement>(`[data-testid="post-submit-message-button"][data-challenge-id="${message.challengeId}"]`)
      ).find((el) => el.getClientRects().length > 0)
      : undefined
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!content || target === undefined || reduced) {
      onClosed()
      return
    }
    const from = content.getBoundingClientRect()
    const to = target.getBoundingClientRect()
    content.style.setProperty('--shrink-x', `${to.left + to.width / 2 - (from.left + from.width / 2)}px`)
    content.style.setProperty('--shrink-y', `${to.top + to.height / 2 - (from.top + from.height / 2)}px`)
    setClosing(true)
    window.setTimeout(onClosed, SHRINK_MS)
  }

  const title = branding?.name ? t('challenges.messageFromOrg', { name: branding.name }) : t('challenges.messageFromYourOrganization')

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close() }}>
      <DialogContent
        ref={contentRef}
        width="md"
        className={`max-h-[90vh] flex flex-col ${closing ? 'post-submit-shrink' : ''}`}
        data-testid="post-submit-message-dialog"
      >
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-auto min-h-[200px]">
          {message && (
            <MarkdownEditor
              key={message.challengeId}
              defaultValue={message.message}
              mode="view"
              noBorder
              className="h-full"
            />
          )}
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={close}>{t('common.close')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
